function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { Component } from '@theme/component';
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
var _controller = _classPrivateFieldLooseKey("controller");
var _scale = _classPrivateFieldLooseKey("scale");
var _initialDistance = _classPrivateFieldLooseKey("initialDistance");
var _startScale = _classPrivateFieldLooseKey("startScale");
var _translate = _classPrivateFieldLooseKey("translate");
var _startPosition = _classPrivateFieldLooseKey("startPosition");
var _startTranslate = _classPrivateFieldLooseKey("startTranslate");
var _isDragging = _classPrivateFieldLooseKey("isDragging");
var _initialized = _classPrivateFieldLooseKey("initialized");
var _animationFrame = _classPrivateFieldLooseKey("animationFrame");
var _lastTapTime = _classPrivateFieldLooseKey("lastTapTime");
var _lastTapPosition = _classPrivateFieldLooseKey("lastTapPosition");
var _hasDraggedBeyondThreshold = _classPrivateFieldLooseKey("hasDraggedBeyondThreshold");
var _hasManualZoom = _classPrivateFieldLooseKey("hasManualZoom");
var _image = _classPrivateFieldLooseKey("image");
var _initResizeListener = _classPrivateFieldLooseKey("initResizeListener");
var _initEventListeners = _classPrivateFieldLooseKey("initEventListeners");
var _handleResize = _classPrivateFieldLooseKey("handleResize");
var _resizeObserver = _classPrivateFieldLooseKey("resizeObserver");
var _handleTouchStart = _classPrivateFieldLooseKey("handleTouchStart");
var _startZoomGestureFromTouches = _classPrivateFieldLooseKey("startZoomGestureFromTouches");
var _startDragGestureFromTouch = _classPrivateFieldLooseKey("startDragGestureFromTouch");
var _storeTapInfo = _classPrivateFieldLooseKey("storeTapInfo");
var _handleDoubleTapFromTouch = _classPrivateFieldLooseKey("handleDoubleTapFromTouch");
var _handleTouchMove = _classPrivateFieldLooseKey("handleTouchMove");
var _processZoomGesture = _classPrivateFieldLooseKey("processZoomGesture");
var _processDragGesture = _classPrivateFieldLooseKey("processDragGesture");
var _handleTouchEnd = _classPrivateFieldLooseKey("handleTouchEnd");
var _constrainTranslation = _classPrivateFieldLooseKey("constrainTranslation");
var _requestUpdateTransform = _classPrivateFieldLooseKey("requestUpdateTransform");
var _cancelAnimationFrame = _classPrivateFieldLooseKey("cancelAnimationFrame");
var _updateTransform = _classPrivateFieldLooseKey("updateTransform");
var _resetZoom = _classPrivateFieldLooseKey("resetZoom");
export class DragZoomWrapper extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Cancel any pending animation frame
     */
    Object.defineProperty(this, _cancelAnimationFrame, {
      value: _cancelAnimationFrame2
    });
    /**
     * Constrain image translation to keep it within the viewport
     */
    Object.defineProperty(this, _constrainTranslation, {
      value: _constrainTranslation2
    });
    /**
     * Process drag gesture from touch
     * @param {Touch} touch
     */
    Object.defineProperty(this, _processDragGesture, {
      value: _processDragGesture2
    });
    /**
     * Process zoom gesture from touches
     * @param {Touch} touch1
     * @param {Touch} touch2
     */
    Object.defineProperty(this, _processZoomGesture, {
      value: _processZoomGesture2
    });
    /**
     * Handle double-tap zoom toggle from touch
     * @param {Touch} touch - The touch where the double-tap occurred
     */
    Object.defineProperty(this, _handleDoubleTapFromTouch, {
      value: _handleDoubleTapFromTouch2
    });
    /**
     * Store tap information for double-tap detection
     * @param {number} currentTime
     * @param {Touch} touch
     */
    Object.defineProperty(this, _storeTapInfo, {
      value: _storeTapInfo2
    });
    /**
     * Start a drag gesture with a single touch
     * @param {Touch} touch
     */
    Object.defineProperty(this, _startDragGestureFromTouch, {
      value: _startDragGestureFromTouch2
    });
    /**
     * Start a zoom gesture with two touches
     * @param {Touch} touch1
     * @param {Touch} touch2
     */
    Object.defineProperty(this, _startZoomGestureFromTouches, {
      value: _startZoomGestureFromTouches2
    });
    Object.defineProperty(this, _initEventListeners, {
      value: _initEventListeners2
    });
    Object.defineProperty(this, _initResizeListener, {
      value: _initResizeListener2
    });
    Object.defineProperty(this, _image, {
      get: _get_image,
      set: void 0
    });
    this.requiredRefs = ['image'];
    Object.defineProperty(this, _controller, {
      writable: true,
      value: new AbortController()
    });
    /** @type {number} */
    Object.defineProperty(this, _scale, {
      writable: true,
      value: DEFAULT_ZOOM
    });
    /** @type {number} */
    Object.defineProperty(this, _initialDistance, {
      writable: true,
      value: 0
    });
    /** @type {number} */
    Object.defineProperty(this, _startScale, {
      writable: true,
      value: DEFAULT_ZOOM
    });
    /** @type {Point} */
    Object.defineProperty(this, _translate, {
      writable: true,
      value: {
        x: 0,
        y: 0
      }
    });
    /** @type {Point} */
    Object.defineProperty(this, _startPosition, {
      writable: true,
      value: {
        x: 0,
        y: 0
      }
    });
    /** @type {Point} */
    Object.defineProperty(this, _startTranslate, {
      writable: true,
      value: {
        x: 0,
        y: 0
      }
    });
    /** @type {boolean} */
    Object.defineProperty(this, _isDragging, {
      writable: true,
      value: false
    });
    /** @type {boolean} */
    Object.defineProperty(this, _initialized, {
      writable: true,
      value: false
    });
    /** @type {number | null} */
    Object.defineProperty(this, _animationFrame, {
      writable: true,
      value: null
    });
    /** @type {number} */
    Object.defineProperty(this, _lastTapTime, {
      writable: true,
      value: 0
    });
    /** @type {Point | null} */
    Object.defineProperty(this, _lastTapPosition, {
      writable: true,
      value: null
    });
    /** @type {boolean} */
    Object.defineProperty(this, _hasDraggedBeyondThreshold, {
      writable: true,
      value: false
    });
    /** @type {boolean} */
    Object.defineProperty(this, _hasManualZoom, {
      writable: true,
      value: false
    });
    Object.defineProperty(this, _handleResize, {
      writable: true,
      value: () => {
        if (!_classPrivateFieldLooseBase(this, _initialized)[_initialized] && isMobileBreakpoint()) {
          _classPrivateFieldLooseBase(this, _initEventListeners)[_initEventListeners]();
        }
        if (_classPrivateFieldLooseBase(this, _initialized)[_initialized]) {
          _classPrivateFieldLooseBase(this, _requestUpdateTransform)[_requestUpdateTransform]();
        }
      }
    });
    Object.defineProperty(this, _resizeObserver, {
      writable: true,
      value: new ResizeObserver(_classPrivateFieldLooseBase(this, _handleResize)[_handleResize])
    });
    /**
     * @param {TouchEvent} event
     */
    Object.defineProperty(this, _handleTouchStart, {
      writable: true,
      value: event => {
        preventDefault(event);
        const touchCount = event.touches.length;
        if (touchCount === 2) {
          // Early exit if touches are invalid
          const touch1 = event.touches[0];
          const touch2 = event.touches[1];
          if (!touch1 || !touch2) return;

          // Avoid object allocation by passing touches directly
          _classPrivateFieldLooseBase(this, _startZoomGestureFromTouches)[_startZoomGestureFromTouches](touch1, touch2);
        } else if (touchCount === 1) {
          const touch = event.touches[0];
          if (!touch) return;

          // Use performance.now() for better precision and performance
          const currentTime = performance.now();
          const timeSinceLastTap = currentTime - _classPrivateFieldLooseBase(this, _lastTapTime)[_lastTapTime];

          // Early exit if too much time has passed
          if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
            _classPrivateFieldLooseBase(this, _storeTapInfo)[_storeTapInfo](currentTime, touch);
            _classPrivateFieldLooseBase(this, _startDragGestureFromTouch)[_startDragGestureFromTouch](touch);
            return;
          }

          // Only check distance if we have a previous tap within time window
          if (_classPrivateFieldLooseBase(this, _lastTapPosition)[_lastTapPosition]) {
            // Distance calculation with early exit
            const distance = getDistance(touch, _classPrivateFieldLooseBase(this, _lastTapPosition)[_lastTapPosition]);
            if (distance < DOUBLE_TAP_DISTANCE) {
              // This is a double-tap, handle zoom toggle
              _classPrivateFieldLooseBase(this, _handleDoubleTapFromTouch)[_handleDoubleTapFromTouch](touch);
              _classPrivateFieldLooseBase(this, _lastTapTime)[_lastTapTime] = 0; // Reset to prevent triple-tap
              _classPrivateFieldLooseBase(this, _lastTapPosition)[_lastTapPosition] = null;
              return;
            }
          }

          // Store tap info for potential double-tap detection
          _classPrivateFieldLooseBase(this, _storeTapInfo)[_storeTapInfo](currentTime, touch);
          _classPrivateFieldLooseBase(this, _startDragGestureFromTouch)[_startDragGestureFromTouch](touch);
        }
      }
    });
    /**
     * @param {TouchEvent} event
     */
    Object.defineProperty(this, _handleTouchMove, {
      writable: true,
      value: event => {
        preventDefault(event);
        const touchCount = event.touches.length;
        if (touchCount === 2) {
          const touch1 = event.touches[0];
          const touch2 = event.touches[1];
          if (touch1 && touch2) {
            _classPrivateFieldLooseBase(this, _processZoomGesture)[_processZoomGesture](touch1, touch2);
          }
        } else if (touchCount === 1 && _classPrivateFieldLooseBase(this, _isDragging)[_isDragging]) {
          const touch = event.touches[0];
          if (touch) {
            _classPrivateFieldLooseBase(this, _processDragGesture)[_processDragGesture](touch);
          }
        }
      }
    });
    /**
     * @param {TouchEvent} event
     */
    Object.defineProperty(this, _handleTouchEnd, {
      writable: true,
      value: event => {
        if (event.touches.length === 0) {
          _classPrivateFieldLooseBase(this, _isDragging)[_isDragging] = false;
          _classPrivateFieldLooseBase(this, _requestUpdateTransform)[_requestUpdateTransform]();
          _classPrivateFieldLooseBase(this, _hasDraggedBeyondThreshold)[_hasDraggedBeyondThreshold] = false;
        }
      }
    });
    /**
     * Request an animation frame to update the transform
     */
    Object.defineProperty(this, _requestUpdateTransform, {
      writable: true,
      value: () => {
        if (!_classPrivateFieldLooseBase(this, _animationFrame)[_animationFrame]) {
          _classPrivateFieldLooseBase(this, _animationFrame)[_animationFrame] = requestAnimationFrame(_classPrivateFieldLooseBase(this, _updateTransform)[_updateTransform]);
        }
      }
    });
    Object.defineProperty(this, _updateTransform, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _animationFrame)[_animationFrame] = null;
        _classPrivateFieldLooseBase(this, _constrainTranslation)[_constrainTranslation]();
        this.style.setProperty('--drag-zoom-scale', _classPrivateFieldLooseBase(this, _scale)[_scale].toString());
        this.style.setProperty('--drag-zoom-translate-x', `${_classPrivateFieldLooseBase(this, _translate)[_translate].x}px`);
        this.style.setProperty('--drag-zoom-translate-y', `${_classPrivateFieldLooseBase(this, _translate)[_translate].y}px`);
      }
    });
    /**
     * Reset zoom to default state (1.5x scale, centered position)
     * Called when zoom is exited/closed
     */
    Object.defineProperty(this, _resetZoom, {
      writable: true,
      value: () => {
        // Reset scale and translation to defaults
        _classPrivateFieldLooseBase(this, _scale)[_scale] = DEFAULT_ZOOM;
        _classPrivateFieldLooseBase(this, _startScale)[_startScale] = DEFAULT_ZOOM;
        _classPrivateFieldLooseBase(this, _translate)[_translate].x = 0;
        _classPrivateFieldLooseBase(this, _translate)[_translate].y = 0;

        // Reset gesture state to prevent interference on next zoom open
        _classPrivateFieldLooseBase(this, _startPosition)[_startPosition] = {
          x: 0,
          y: 0
        };
        _classPrivateFieldLooseBase(this, _startTranslate)[_startTranslate] = {
          x: 0,
          y: 0
        };
        _classPrivateFieldLooseBase(this, _isDragging)[_isDragging] = false;
        _classPrivateFieldLooseBase(this, _lastTapTime)[_lastTapTime] = 0;
        _classPrivateFieldLooseBase(this, _lastTapPosition)[_lastTapPosition] = null;
        _classPrivateFieldLooseBase(this, _hasDraggedBeyondThreshold)[_hasDraggedBeyondThreshold] = false;

        // Update CSS properties to reflect reset state
        this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
        this.style.setProperty('--drag-zoom-translate-x', '0px');
        this.style.setProperty('--drag-zoom-translate-y', '0px');
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    if (!_classPrivateFieldLooseBase(this, _image)[_image]) return;
    _classPrivateFieldLooseBase(this, _initResizeListener)[_initResizeListener]();
    window.addEventListener(DialogCloseEvent.eventName, _classPrivateFieldLooseBase(this, _resetZoom)[_resetZoom]);
    if (!isMobileBreakpoint()) return;
    _classPrivateFieldLooseBase(this, _initEventListeners)[_initEventListeners]();
    _classPrivateFieldLooseBase(this, _updateTransform)[_updateTransform]();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, _classPrivateFieldLooseBase(this, _resetZoom)[_resetZoom]);
    _classPrivateFieldLooseBase(this, _controller)[_controller].abort();
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].disconnect();
    _classPrivateFieldLooseBase(this, _cancelAnimationFrame)[_cancelAnimationFrame]();
  }
  destroy() {
    _classPrivateFieldLooseBase(this, _controller)[_controller].abort();
    _classPrivateFieldLooseBase(this, _cancelAnimationFrame)[_cancelAnimationFrame]();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function _get_image() {
  return this.refs.image;
}
function _initResizeListener2() {
  _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].observe(this);
}
function _initEventListeners2() {
  if (_classPrivateFieldLooseBase(this, _initialized)[_initialized]) return;
  _classPrivateFieldLooseBase(this, _initialized)[_initialized] = true;
  const {
    signal
  } = _classPrivateFieldLooseBase(this, _controller)[_controller];
  const options = {
    passive: false,
    signal
  };
  this.addEventListener('touchstart', _classPrivateFieldLooseBase(this, _handleTouchStart)[_handleTouchStart], options);
  this.addEventListener('touchmove', _classPrivateFieldLooseBase(this, _handleTouchMove)[_handleTouchMove], options);
  this.addEventListener('touchend', _classPrivateFieldLooseBase(this, _handleTouchEnd)[_handleTouchEnd], options);

  // Initialize transform immediately
  _classPrivateFieldLooseBase(this, _updateTransform)[_updateTransform]();
}
function _startZoomGestureFromTouches2(touch1, touch2) {
  // Calculate initial distance between touches
  _classPrivateFieldLooseBase(this, _initialDistance)[_initialDistance] = getDistance(touch1, touch2);
  _classPrivateFieldLooseBase(this, _startScale)[_startScale] = _classPrivateFieldLooseBase(this, _scale)[_scale];
  _classPrivateFieldLooseBase(this, _isDragging)[_isDragging] = false;
}
function _startDragGestureFromTouch2(touch) {
  _classPrivateFieldLooseBase(this, _startPosition)[_startPosition] = {
    x: touch.clientX,
    y: touch.clientY
  };
  _classPrivateFieldLooseBase(this, _startTranslate)[_startTranslate] = {
    x: _classPrivateFieldLooseBase(this, _translate)[_translate].x,
    y: _classPrivateFieldLooseBase(this, _translate)[_translate].y
  };
  _classPrivateFieldLooseBase(this, _isDragging)[_isDragging] = true;
  _classPrivateFieldLooseBase(this, _hasDraggedBeyondThreshold)[_hasDraggedBeyondThreshold] = false;
}
function _storeTapInfo2(currentTime, touch) {
  _classPrivateFieldLooseBase(this, _lastTapTime)[_lastTapTime] = currentTime;
  _classPrivateFieldLooseBase(this, _lastTapPosition)[_lastTapPosition] = {
    x: touch.clientX,
    y: touch.clientY
  };
}
function _handleDoubleTapFromTouch2(touch) {
  const containerCenter = {
    x: this.clientWidth / 2,
    y: this.clientHeight / 2
  };
  let targetZoom;

  // If manual zoom has been used, reset to 1x
  if (_classPrivateFieldLooseBase(this, _hasManualZoom)[_hasManualZoom]) {
    targetZoom = MIN_ZOOM; // 1x
    _classPrivateFieldLooseBase(this, _hasManualZoom)[_hasManualZoom] = false; // Reset the flag
    _classPrivateFieldLooseBase(this, _translate)[_translate] = {
      x: 0,
      y: 0
    }; // Center the image
  } else {
    // Toggle between zoom levels: 1x ↔ 1.5x
    const tolerance = 0.05; // Small tolerance for floating point comparison

    if (Math.abs(_classPrivateFieldLooseBase(this, _scale)[_scale] - MIN_ZOOM) < tolerance) {
      // Currently at 1x, go to 1.5x
      targetZoom = DEFAULT_ZOOM;
    } else {
      // Currently at 1.5x or any other level, go to 1x
      targetZoom = MIN_ZOOM;
    }
  }

  // If we're not going to 1x, adjust translation to center zoom on the tap point
  if (targetZoom !== MIN_ZOOM) {
    const oldScale = _classPrivateFieldLooseBase(this, _scale)[_scale];
    _classPrivateFieldLooseBase(this, _scale)[_scale] = Math.min(MAX_ZOOM, targetZoom);

    // Calculate the distance from tap point to container center
    const distanceFromCenter = {
      x: touch.clientX - containerCenter.x,
      y: touch.clientY - containerCenter.y
    };

    // Adjust translation to center zoom on the tap point
    const scaleDelta = _classPrivateFieldLooseBase(this, _scale)[_scale] / oldScale - 1.0;
    _classPrivateFieldLooseBase(this, _translate)[_translate].x -= distanceFromCenter.x * scaleDelta / _classPrivateFieldLooseBase(this, _scale)[_scale];
    _classPrivateFieldLooseBase(this, _translate)[_translate].y -= distanceFromCenter.y * scaleDelta / _classPrivateFieldLooseBase(this, _scale)[_scale];
  } else {
    // Going to 1x, set the scale and center the image
    _classPrivateFieldLooseBase(this, _scale)[_scale] = targetZoom;
    _classPrivateFieldLooseBase(this, _translate)[_translate] = {
      x: 0,
      y: 0
    }; // Center the image when going to 1x
  }
  _classPrivateFieldLooseBase(this, _requestUpdateTransform)[_requestUpdateTransform]();
}
function _processZoomGesture2(touch1, touch2) {
  // Calculate midpoint directly without object allocation
  const midX = (touch1.clientX + touch2.clientX) / 2;
  const midY = (touch1.clientY + touch2.clientY) / 2;

  // Calculate current distance between touches
  const currentDistance = getDistance(touch1, touch2);
  const oldScale = _classPrivateFieldLooseBase(this, _scale)[_scale];

  // Calculate and apply new scale
  const newScale = currentDistance / _classPrivateFieldLooseBase(this, _initialDistance)[_initialDistance] * _classPrivateFieldLooseBase(this, _startScale)[_startScale];
  _classPrivateFieldLooseBase(this, _scale)[_scale] = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

  // Mark that manual zoom has been used
  _classPrivateFieldLooseBase(this, _hasManualZoom)[_hasManualZoom] = true;

  // Adjust translation to keep the pinch midpoint stationary
  const containerCenterX = this.clientWidth / 2;
  const containerCenterY = this.clientHeight / 2;
  const distanceFromCenterX = midX - containerCenterX;
  const distanceFromCenterY = midY - containerCenterY;

  // Calculate how the image position needs to change to keep the midpoint stationary
  const scaleDelta = _classPrivateFieldLooseBase(this, _scale)[_scale] / oldScale - 1.0;

  // Apply correction to prevent zooming on the opposite side of the midpoint
  _classPrivateFieldLooseBase(this, _translate)[_translate].x -= distanceFromCenterX * scaleDelta / _classPrivateFieldLooseBase(this, _scale)[_scale];
  _classPrivateFieldLooseBase(this, _translate)[_translate].y -= distanceFromCenterY * scaleDelta / _classPrivateFieldLooseBase(this, _scale)[_scale];
  _classPrivateFieldLooseBase(this, _requestUpdateTransform)[_requestUpdateTransform]();
  _classPrivateFieldLooseBase(this, _isDragging)[_isDragging] = false;
}
function _processDragGesture2(touch) {
  // Check if we've moved beyond the drag threshold
  const distance = getDistance(touch, _classPrivateFieldLooseBase(this, _startPosition)[_startPosition]);
  if (!_classPrivateFieldLooseBase(this, _hasDraggedBeyondThreshold)[_hasDraggedBeyondThreshold] && distance < DRAG_THRESHOLD) {
    // Movement is too small, don't process as drag yet
    return;
  }
  _classPrivateFieldLooseBase(this, _hasDraggedBeyondThreshold)[_hasDraggedBeyondThreshold] = true;

  // Calculate movement deltas for translation
  const dx = touch.clientX - _classPrivateFieldLooseBase(this, _startPosition)[_startPosition].x;
  const dy = touch.clientY - _classPrivateFieldLooseBase(this, _startPosition)[_startPosition].y;

  // Calculate new translation directly
  _classPrivateFieldLooseBase(this, _translate)[_translate].x = _classPrivateFieldLooseBase(this, _startTranslate)[_startTranslate].x + dx / _classPrivateFieldLooseBase(this, _scale)[_scale];
  _classPrivateFieldLooseBase(this, _translate)[_translate].y = _classPrivateFieldLooseBase(this, _startTranslate)[_startTranslate].y + dy / _classPrivateFieldLooseBase(this, _scale)[_scale];
  _classPrivateFieldLooseBase(this, _requestUpdateTransform)[_requestUpdateTransform]();
}
function _constrainTranslation2() {
  const containerWidth = this.clientWidth;
  const containerHeight = this.clientHeight;
  if (!containerWidth || !containerHeight || !_classPrivateFieldLooseBase(this, _image)[_image]) return;

  // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
  _classPrivateFieldLooseBase(this, _scale)[_scale] = clamp(_classPrivateFieldLooseBase(this, _scale)[_scale], MIN_ZOOM, MAX_ZOOM);

  // At minimum zoom (1x), the full image should be visible with no dragging allowed
  if (_classPrivateFieldLooseBase(this, _scale)[_scale] <= MIN_ZOOM) {
    _classPrivateFieldLooseBase(this, _translate)[_translate].x = 0;
    _classPrivateFieldLooseBase(this, _translate)[_translate].y = 0;
    return;
  }

  // Get wrapper dimensions
  const wrapperRect = this.getBoundingClientRect();

  // Calculate ACTUAL image content dimensions at current zoom
  // The image element may fill the wrapper, but the content has its own aspect ratio
  const imageElement = _classPrivateFieldLooseBase(this, _image)[_image];
  let naturalWidth, naturalHeight;

  // Try to get natural dimensions
  if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
    naturalWidth = imageElement.naturalWidth;
    naturalHeight = imageElement.naturalHeight;
  } else {
    // Fallback: assume square image if we can't get natural dimensions
    naturalWidth = wrapperRect.width;
    naturalHeight = wrapperRect.width;
  }

  // Calculate how the image fits within the wrapper (object-fit: contain behavior)
  const imageAspectRatio = naturalWidth / naturalHeight;
  const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;
  let actualImageWidth, actualImageHeight;
  if (imageAspectRatio > wrapperAspectRatio) {
    // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
    actualImageWidth = wrapperRect.width;
    actualImageHeight = actualImageWidth / imageAspectRatio;
  } else {
    // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
    actualImageHeight = wrapperRect.height;
    actualImageWidth = actualImageHeight * imageAspectRatio;
  }

  // Apply current zoom scale
  const scaledImageWidth = actualImageWidth * _classPrivateFieldLooseBase(this, _scale)[_scale];
  const scaledImageHeight = actualImageHeight * _classPrivateFieldLooseBase(this, _scale)[_scale];

  // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
  // If image content is larger than wrapper, calculate max translation directly

  const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
  const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

  // Max translation is half the overflow (since image starts centered)
  const maxTranslateX = horizontalOverflow / 2 / _classPrivateFieldLooseBase(this, _scale)[_scale];
  const maxTranslateY = verticalOverflow / 2 / _classPrivateFieldLooseBase(this, _scale)[_scale];

  // Apply symmetric constraints (object-fit: contain behavior)
  // Image starts centered, can move maxTranslate in each direction
  _classPrivateFieldLooseBase(this, _translate)[_translate].x = clamp(_classPrivateFieldLooseBase(this, _translate)[_translate].x, -maxTranslateX, maxTranslateX);
  _classPrivateFieldLooseBase(this, _translate)[_translate].y = clamp(_classPrivateFieldLooseBase(this, _translate)[_translate].y, -maxTranslateY, maxTranslateY);

  // Apply final transforms to CSS
  this.style.setProperty('--drag-zoom-scale', _classPrivateFieldLooseBase(this, _scale)[_scale].toString());
  this.style.setProperty('--drag-zoom-translate-x', `${_classPrivateFieldLooseBase(this, _translate)[_translate].x}px`);
  this.style.setProperty('--drag-zoom-translate-y', `${_classPrivateFieldLooseBase(this, _translate)[_translate].y}px`);
}
function _cancelAnimationFrame2() {
  if (_classPrivateFieldLooseBase(this, _animationFrame)[_animationFrame]) {
    cancelAnimationFrame(_classPrivateFieldLooseBase(this, _animationFrame)[_animationFrame]);
    _classPrivateFieldLooseBase(this, _animationFrame)[_animationFrame] = null;
  }
}
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */point1.x ?? /** @type {Touch} */point1.clientX;
  const y1 = /** @type {Point} */point1.y ?? /** @type {Touch} */point1.clientY;
  const x2 = /** @type {Point} */point2.x ?? /** @type {Touch} */point2.clientX;
  const y2 = /** @type {Point} */point2.y ?? /** @type {Touch} */point2.clientY;
  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}
if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
